import { FunctionComponent, memo, useContext, useEffect, useRef } from "react";
import * as Yup from "yup";
import { IProps } from "./phrase-trigger-threshold-form.types";
import { FieldLabelTooltip, GridContext } from "@edgetier/client-components";
import { Field, Form, Formik } from "formik";
import { Button, FieldError } from "@edgetier/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowFromBottom, faCheck } from "@fortawesome/pro-solid-svg-icons";
import { debounce } from "throttle-debounce";
import { trackUser } from "utilities/track-user";
import "./phrase-trigger-threshold-form.scss";

/**
 * Display an input for setting the threshold value of a specific sample
 * @param props.tagName         Tag name for the current phrase trigger
 * @param props.defaultValue
 * @param props.thresholdSubmit Callback to update phrase trigger
 */
const PhraseTriggerThresholdForm: FunctionComponent<IProps> = ({ initialValue, tagName, thresholdSubmit }) => {
    const { expandRow, rows } = useContext(GridContext);
    const rowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);

    return (
        <div className="phrase-trigger-threshold-form" ref={rowRef}>
            <Formik<{ thresholdValue: number }>
                onSubmit={({ thresholdValue }) => {
                    const newExpandedRowId = [...(rows ?? [])]
                        .reverse()
                        .find((item) => item.score >= thresholdValue / 100)?.sentenceId;

                    thresholdSubmit(parseFloat(thresholdValue.toString()));
                    expandRow(newExpandedRowId);
                }}
                enableReinitialize
                initialValues={{ thresholdValue: initialValue }}
                validationSchema={Yup.object().shape({
                    thresholdValue: Yup.number()
                        .min(60, "Value should be greater than 60")
                        .max(100, "Value should be lower than 100")
                        .required("Required"),
                })}
            >
                {({ isValid, isSubmitting, dirty }) => (
                    <>
                        <Form name="phrase-trigger-threshold-form">
                            <div className="field">
                                <FieldError name="thresholdValue" />
                                <FieldLabelTooltip
                                    label="Type Threshold Value"
                                    name="thresholdValue"
                                    placement="top-center"
                                    tooltipContent={
                                        <>
                                            Interactions with matches above this threshold will be tagged with &ldquo;
                                            {tagName}&rdquo;
                                        </>
                                    }
                                />
                                <Field
                                    id="thresholdValue"
                                    name="thresholdValue"
                                    type="number"
                                    placeholder="Threshold value"
                                    autoComplete="off"
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    onKeyUp={() =>
                                        debounce(500, () => {
                                            trackUser("Type Threshold Value");
                                        })
                                    }
                                />
                            </div>

                            <Button
                                icon={faCheck}
                                styleName="positive"
                                outline
                                type="submit"
                                size="normal"
                                disabled={!isValid || isSubmitting || !dirty}
                            >
                                Apply
                            </Button>
                        </Form>
                        <div className="phrase-trigger-threshold-form__information">
                            <FontAwesomeIcon icon={faArrowFromBottom} />
                            <span>Sentences above the line are included.</span>
                        </div>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default memo(PhraseTriggerThresholdForm);
